import { TipsAndUpdates } from "@mui/icons-material";
import { AlertTitle, Button, Stack, Tooltip, Typography } from "@mui/material";
import { Collapsible } from "components/common/Collapsible";
import DismissableAlert, { DismissableAlertHandle } from "components/common/DismissableAlert";
import { useFormikContext } from "formik";
import { useGlobalAlert } from "hooks/useAlert";
import { useConfirmation } from "hooks/useConfirmation";
import { useCallback, useRef } from "react";
import { api } from "services/api.service";
import { Inventory, VertexAiResponse } from "types/inventory";

export default function CosmeticGradeSuggestion({ inventoryMetadata }: { inventoryMetadata: VertexAiResponse }) {
  const dismissableAlertRef = useRef<DismissableAlertHandle>(null);
  const { values } = useFormikContext<Inventory>();
  const { confirm } = useConfirmation();

  const handleApply = useCallback(() => {
    confirm({
      title: "Apply Suggested Condition",
      message: `This will update the condition to ${inventoryMetadata.grade}`,
      handler: async () => {
        await api
          .post(`/inventories/${values.id}/apply_suggested_condition`)
          .then(() => useGlobalAlert.alertSuccess("Condition suggestion applied."))
          .catch(() => useGlobalAlert.alertError("Failed to apply condition suggestion."));
      },
    });
  }, [confirm, values, inventoryMetadata.grade]);

  return (
    <DismissableAlert action={<></>} severity="info" icon={<TipsAndUpdates />} ref={dismissableAlertRef}>
      <AlertTitle variant="subtitle1" sx={{ fontWeight: 600 }}>
        AI Suggested Grade: {inventoryMetadata.grade}
      </AlertTitle>
      <Typography variant="subtitle2">
        <b>Reason:</b> {inventoryMetadata.summary}
      </Typography>
      <Stack direction="row" gap={1}>
        <Tooltip arrow placement="top-end" title="Confidence level is AI's certainty for this suggestion.">
          <Typography sx={{ fontWeight: "bold", textDecoration: "underline", textDecorationStyle: "dashed" }}>
            Confidence:
          </Typography>
        </Tooltip>
        {inventoryMetadata.confidence}%
      </Stack>

      <Collapsible buttonProps={{ size: "small" }}>
        {inventoryMetadata?.defects &&
          inventoryMetadata?.defects.map((defect, idx) => (
            <Typography key={idx} color="text.secondary" variant="body2">
              - {defect.severity} {defect.defectType} on {defect.location}
            </Typography>
          ))}
      </Collapsible>
      <Stack direction="row" gap={2} justifyContent="flex-end">
        <Button variant="text" onClick={dismissableAlertRef.current?.dismiss}>
          Dismiss
        </Button>
        <Button disabled={values.condition === inventoryMetadata.grade} variant="contained" onClick={handleApply}>
          Apply
        </Button>
      </Stack>
    </DismissableAlert>
  );
}
